import React, { ReactChild, ReactElement } from 'react';
import { useForm, FormProvider, FieldValues, UseFormProps } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Tag, Button, Spin, App } from 'antd';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import saveTranslation from '../../../constants/SaveTranslation.json';
import Page from './Page';
import PageHookFormDirtyHandler from './PageHookFormDirtyHandler';
import { updatePageFormDirtyState } from '../../../actions/catalogue/catalogue/update';
import { AsyncDispatch } from '../../../../types/global';
import { ApplicationState } from '../../../reducers';
import { useBlockerConfirm } from '../../../hooks/useBlockerConfirm';
import { hasBrandEditPermission } from '../../../selectors/userSelector';
import { FormSaveShortcut } from '../Forms/FormSaveShortcut';
import { useAppDispatch } from '../../../store/ReduxStore';

export type CustomHookFormActions = {
  setSubmitPending: () => void;
  setSubmitSuccess: () => void;
  setSubmitError: (message?: string) => void;
  reset: (values?: FieldValues) => void;
};

type PageHookFormProps = {
  initialValues: FieldValues;
  validationSchema?: any;
  onSubmit: (values: FieldValues, actions: CustomHookFormActions) => void;
  onCancel?: () => void;
  children: (methods: any) => ReactChild | null;
  renderHeader?: (methods: any) => ReactElement;
  handleSaveButtonEnabled?: (methods: any) => boolean;
  showAnalysis?: boolean;
  fetchingData?: boolean;
  contentNoSpacing?: boolean;
  contentNoScroll?: boolean;
  enableReinitialize?: boolean;
  showExpandButton?: boolean;
  showNoError?: boolean;
  mode?: 'onChange' | 'onBlur' | 'onSubmit' | 'onTouched' | 'all';
};

enum FormSubmissionStatus {
  'EDIT' = 'EDIT',
  'SUCCESS' = 'SUCCESS',
  'ERROR' = 'ERROR',
}

const PageHookForm: React.FC<PageHookFormProps> = props => {
  const dispatch: AsyncDispatch = useAppDispatch();
  const navigate = useNavigate();
  const { modal } = App.useApp();

  const [nextRouteLocation, setNextRouteLocation] = React.useState<any>(null);
  const [navigateModalVisible, setNavigateModalVisible] = React.useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = React.useState<string | undefined>(undefined);
  const [submissionStatus, setSubmissionStatus] = React.useState<FormSubmissionStatus>(
    FormSubmissionStatus.EDIT
  );
  const [isFormSubmitting, setIsFormSubmitting] = React.useState(false);

  const { brandId, pageFormDirty, brandEditPermission } = useSelector(
    (state: ApplicationState) => ({
      brandId: state.parent.brands.selectedBrandId,
      pageFormDirty: state.catalogue.catalogue.pageFormDirty,
      brandEditPermission: hasBrandEditPermission(state),
    })
  );

  const formConfig: UseFormProps<FieldValues> = {
    defaultValues: props.initialValues,
    mode: props.mode || 'onTouched',
    resolver: props.validationSchema ? yupResolver(props.validationSchema) : undefined,
  };

  const formMethods = useForm(formConfig);
  const { handleSubmit, reset, formState } = formMethods;
  const { isDirty, isValid, isSubmitting, errors } = formState;

  const { blockerActive, next, onConfirm, onCancel } = useBlockerConfirm(
    pageFormDirty && !nextRouteLocation
  );

  React.useEffect(() => {
    if (nextRouteLocation) navigate(nextRouteLocation);
  }, [navigate, nextRouteLocation]);

  React.useEffect(() => {
    return () => {
      dispatch(updatePageFormDirtyState(false));
      if (blockerActive) onCancel();
    };
  }, []);

  React.useEffect(() => {
    if (props.enableReinitialize) {
      reset(props.initialValues);
    }
  }, [props.initialValues, props.enableReinitialize, reset]);

  React.useEffect(() => {
    const showModal = (next: string, onConfirm: () => void, onCancel: () => void) => {
      if (next === nextRouteLocation) {
        setNextRouteLocation('');
        onConfirm();
        return;
      }
      setNavigateModalVisible(true);
      modal.confirm({
        title: saveTranslation.closeWarningTitle,
        icon: <ExclamationCircleOutlined />,
        onOk() {
          setNextRouteLocation(next);
          setNavigateModalVisible(false);
          onConfirm();
        },
        onCancel() {
          setNextRouteLocation('');
          setNavigateModalVisible(false);
          onCancel();
        },
      });
    };
    if (next && !navigateModalVisible) showModal(next, onConfirm, onCancel);
  }, [modal, navigateModalVisible, next, nextRouteLocation, onCancel, onConfirm]);

  const onSubmitHandler = (values: FieldValues) => {
    const actions: CustomHookFormActions = {
      setSubmitPending: () => {
        setIsFormSubmitting(true);
        setSubmitErrorMessage(undefined);
      },
      setSubmitSuccess: () => {
        reset(values);
        setSubmissionStatus(FormSubmissionStatus.SUCCESS);
        setIsFormSubmitting(false);
        setTimeout(() => setSubmissionStatus(FormSubmissionStatus.EDIT), 2500);
      },
      setSubmitError: (message?: string) => {
        setIsFormSubmitting(false);
        if (props.showNoError) {
          setSubmissionStatus(FormSubmissionStatus.EDIT);
        } else {
          setSubmissionStatus(FormSubmissionStatus.ERROR);
          setSubmitErrorMessage(message);
        }
      },
      reset,
    };
    props.onSubmit(values, actions);
  };

  if (props.fetchingData) {
    return (
      <div className="flex justify-center items-center flex-1">
        <Spin size="large" />
      </div>
    );
  }

  const saveButtonEnabled = props.handleSaveButtonEnabled
    ? props.handleSaveButtonEnabled(formMethods)
    : isDirty && (!brandId || (brandId && brandEditPermission));

  const show =
    (isDirty && FormSubmissionStatus.EDIT === submissionStatus) ||
    FormSubmissionStatus.SUCCESS === submissionStatus ||
    FormSubmissionStatus.ERROR === submissionStatus;

  return (
    <FormProvider {...formMethods}>
      <Page
        showExpandButton={props.showExpandButton}
        showAnalysis={props.showAnalysis}
        contentNoSpacing={props.contentNoSpacing}
        contentNoScroll={props.contentNoScroll}
        renderHeader={props.renderHeader && props.renderHeader(formMethods)}
        renderBottom={
          show ? (
            <React.Fragment>
              <div
                className={classNames('status-bar flex items-center', {
                  success: submissionStatus === FormSubmissionStatus.SUCCESS,
                  error: submissionStatus === FormSubmissionStatus.ERROR,
                })}
              >
                {submissionStatus === FormSubmissionStatus.ERROR && !isSubmitting && (
                  <Tag color="red">{submitErrorMessage || saveTranslation.errorSaving}</Tag>
                )}
              </div>
              <div className="page__bottom-save flex items-center">
                <div className="flex items-center flex-1">
                  {!isValid && Object.keys(errors).length > 0 && (
                    <Tag color="red">{saveTranslation.fieldsMissing}</Tag>
                  )}
                  <div className="flex-1 flex justify-end">
                    <Button
                      className="page__bottom-save-cancel"
                      onClick={() => {
                        if (props.onCancel) {
                          props.onCancel();
                        }
                        reset(props.initialValues);
                        setSubmissionStatus(FormSubmissionStatus.EDIT);
                      }}
                    >
                      {saveTranslation.cancel}
                    </Button>

                    <Button
                      data-testid="form-save"
                      type="primary"
                      onClick={handleSubmit(onSubmitHandler)}
                      disabled={!saveButtonEnabled}
                      loading={isSubmitting || isFormSubmitting}
                    >
                      {saveTranslation.save}
                    </Button>
                  </div>
                </div>
              </div>
            </React.Fragment>
          ) : null
        }
      >
        <PageHookFormDirtyHandler />
        <FormSaveShortcut onSave={handleSubmit(onSubmitHandler)} enabled={saveButtonEnabled} />

        {props.children({ ...formMethods, formState })}
      </Page>
    </FormProvider>
  );
};

export default PageHookForm;
